import { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import Image from '@tiptap/extension-image';
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Heading1,
  Heading2,
  Undo2,
  Redo2,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Link as LinkIcon,
  ImagePlus,
  Underline as UnderlineIcon
} from 'lucide-react';

type Props = {
  assignmentId: number;
  readOnly?: boolean;
  isSubmitting?: boolean;
  initialContent?: string;
  onSubmit?: (html: string) => void;
};

const toolBtn =
  'inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-900 hover:text-white transition-colors disabled:opacity-40';

export default function StudentTiptapEditorPane({
  assignmentId,
  readOnly = false,
  isSubmitting = false,
  initialContent = '<p></p>',
  onSubmit
}: Props) {
  const imageInputRef = useRef<HTMLInputElement | null>(null);
  const insertImageFromFileRef = useRef<(file: File) => Promise<void>>(async () => {});

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image,
      TextAlign.configure({
        types: ['heading', 'paragraph']
      })
    ],
    content: initialContent,
    editable: !readOnly,
    editorProps: {
      handlePaste(_view, event) {
        const e = event as ClipboardEvent;
        const cd = e.clipboardData;
        if (!cd) return false;
        for (let i = 0; i < cd.items.length; i++) {
          const item = cd.items[i];
          if (item.kind === 'file' && item.type.startsWith('image/')) {
            const file = item.getAsFile();
            if (file) {
              e.preventDefault();
              void insertImageFromFileRef.current(file);
              return true;
            }
          }
        }
        return false;
      }
    }
  });

  useEffect(() => {
    if (!editor) return;
    insertImageFromFileRef.current = async (file: File) => {
      if (readOnly) return;
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(new Error('file_read_failed'));
        reader.readAsDataURL(file);
      });
      if (!dataUrl) return;
      editor.chain().focus().setImage({ src: dataUrl }).run();
    };
  }, [editor, readOnly]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!readOnly);
  }, [editor, readOnly]);

  useEffect(() => {
    if (!editor) return;
    editor.commands.setContent(initialContent || '<p></p>');
  }, [editor, assignmentId, initialContent]);

  const toggleLink = () => {
    if (!editor || readOnly) return;
    const previousUrl = editor.getAttributes('link').href || '';
    const url = window.prompt('Link-URL einfügen', previousUrl);
    if (url === null) return;
    const trimmed = url.trim();
    if (!trimmed) {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: trimmed }).run();
  };

  const insertImageFromUrl = () => {
    if (!editor || readOnly) return;
    const url = window.prompt('Bild-URL einfügen');
    if (!url) return;
    const trimmed = url.trim();
    if (!trimmed) return;
    editor.chain().focus().setImage({ src: trimmed }).run();
  };

  const openImageFilePicker = () => {
    if (readOnly) return;
    imageInputRef.current?.click();
  };

  const insertImageFromFile = async (file?: File | null) => {
    if (!file) return;
    await insertImageFromFileRef.current(file);
  };

  const buttonClass = (active = false) =>
    `${toolBtn} ${active ? 'bg-gray-900 text-white border-gray-900' : ''}`;

  return (
    <div className="h-full flex flex-col">
      <div className="px-3 py-2 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-1">
          <button type="button" className={buttonClass(editor?.isActive('bold'))} onClick={() => editor?.chain().focus().toggleBold().run()} disabled={!editor || readOnly}>
            <Bold className="h-4 w-4" />
          </button>
          <button type="button" className={buttonClass(editor?.isActive('italic'))} onClick={() => editor?.chain().focus().toggleItalic().run()} disabled={!editor || readOnly}>
            <Italic className="h-4 w-4" />
          </button>
          <button type="button" className={buttonClass(editor?.isActive('underline'))} onClick={() => editor?.chain().focus().toggleUnderline().run()} disabled={!editor || readOnly}>
            <UnderlineIcon className="h-4 w-4" />
          </button>
          <button type="button" className={buttonClass(editor?.isActive('heading', { level: 1 }))} onClick={() => editor?.chain().focus().toggleHeading({ level: 1 }).run()} disabled={!editor || readOnly}>
            <Heading1 className="h-4 w-4" />
          </button>
          <button type="button" className={buttonClass(editor?.isActive('heading', { level: 2 }))} onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()} disabled={!editor || readOnly}>
            <Heading2 className="h-4 w-4" />
          </button>
          <button type="button" className={buttonClass(editor?.isActive({ textAlign: 'left' }))} onClick={() => editor?.chain().focus().setTextAlign('left').run()} disabled={!editor || readOnly}>
            <AlignLeft className="h-4 w-4" />
          </button>
          <button type="button" className={buttonClass(editor?.isActive({ textAlign: 'center' }))} onClick={() => editor?.chain().focus().setTextAlign('center').run()} disabled={!editor || readOnly}>
            <AlignCenter className="h-4 w-4" />
          </button>
          <button type="button" className={buttonClass(editor?.isActive({ textAlign: 'right' }))} onClick={() => editor?.chain().focus().setTextAlign('right').run()} disabled={!editor || readOnly}>
            <AlignRight className="h-4 w-4" />
          </button>
          <button type="button" className={buttonClass(editor?.isActive({ textAlign: 'justify' }))} onClick={() => editor?.chain().focus().setTextAlign('justify').run()} disabled={!editor || readOnly}>
            <AlignJustify className="h-4 w-4" />
          </button>
          <button type="button" className={buttonClass(editor?.isActive('bulletList'))} onClick={() => editor?.chain().focus().toggleBulletList().run()} disabled={!editor || readOnly}>
            <List className="h-4 w-4" />
          </button>
          <button type="button" className={buttonClass(editor?.isActive('orderedList'))} onClick={() => editor?.chain().focus().toggleOrderedList().run()} disabled={!editor || readOnly}>
            <ListOrdered className="h-4 w-4" />
          </button>
          <button type="button" className={buttonClass(editor?.isActive('link'))} onClick={toggleLink} disabled={!editor || readOnly}>
            <LinkIcon className="h-4 w-4" />
          </button>
          <button type="button" className={buttonClass()} onClick={openImageFilePicker} disabled={!editor || readOnly}>
            <ImagePlus className="h-4 w-4" />
          </button>
          <button
            type="button"
            className="inline-flex h-8 items-center justify-center rounded-md border border-gray-300 bg-white px-2 text-xs font-medium text-gray-700 hover:bg-gray-900 hover:text-white transition-colors disabled:opacity-40"
            onClick={insertImageFromUrl}
            disabled={!editor || readOnly}
          >
            URL
          </button>
          <button type="button" className={buttonClass()} onClick={() => editor?.chain().focus().undo().run()} disabled={!editor || readOnly}>
            <Undo2 className="h-4 w-4" />
          </button>
          <button type="button" className={buttonClass()} onClick={() => editor?.chain().focus().redo().run()} disabled={!editor || readOnly}>
            <Redo2 className="h-4 w-4" />
          </button>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              await insertImageFromFile(file);
              e.target.value = '';
            }}
          />
        </div>
        {readOnly ? (
          <p className="text-xs text-gray-500">Read-only</p>
        ) : (
          <button
            type="button"
            className="btn-primary text-xs"
            disabled={isSubmitting || !editor || !onSubmit}
            onClick={() => onSubmit?.(editor?.getHTML() || '<p></p>')}
          >
            {isSubmitting ? 'Reiche ein …' : 'Als Abgabe einreichen'}
          </button>
        )}
      </div>
      <div className="flex-1 overflow-auto bg-gray-50">
        <div className="h-full w-full p-2">
          <div className="h-full w-full rounded-lg border border-gray-200 bg-white p-4">
            <EditorContent
              editor={editor}
              className="h-full [&_.ProseMirror]:h-full [&_.ProseMirror]:min-h-[60vh] [&_.ProseMirror]:outline-none [&_.ProseMirror]:text-gray-900 [&_.ProseMirror_p]:my-2"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

